/**
 * GraphemeIndexMapper.ts
 * 
 * Consolidates grapheme index mapping utilities:
 * - Page grapheme boundary extraction
 * - Cursor-on-page detection (with empty line edge cases)
 * - Click grapheme index validation
 * 
 * REFACTORING_ROADMAP.md Step 9
 * Extracted from EdgeDocsCanvas.V3.tsx to reduce duplication
 * 
 * @module GraphemeIndexMapper
 */

// ============================================================================
// TYPES
// ============================================================================

/**
 * Minimal interface for lines that have grapheme indices
 */
export interface GraphemeIndexedLine {
  start_grapheme_index: number;
  end_grapheme_index: number;
  [key: string]: unknown; // Allow additional properties
}

/**
 * Result of extracting page grapheme boundaries
 */
export interface PageGraphemeBounds {
  /** First grapheme index on the page */
  pageStartGrapheme: number;
  /** Last grapheme index on the page */
  pageEndGrapheme: number;
  /** Total number of lines on the page */
  lineCount: number;
  /** Whether the last line is empty (start === end) */
  lastLineIsEmpty: boolean;
  /** Reference to the last line (for additional checks) */
  lastLine: GraphemeIndexedLine | null;
}

/**
 * Result of checking if cursor is on a page
 */
export interface CursorOnPageResult {
  /** Whether the cursor is on this page */
  cursorOnPage: boolean;
  /** Page grapheme boundaries used for the check */
  bounds: PageGraphemeBounds;
  /** Whether cursor is at start of an empty line (edge case) */
  cursorAtEmptyLineStart: boolean;
}

/**
 * Result of validating a click grapheme index
 */
export interface ValidatedGraphemeIndex {
  /** The validated/adjusted grapheme index */
  graphemeIndex: number;
  /** Whether adjustment was needed */
  wasAdjusted: boolean;
  /** Warning message if adjustment was made */
  warning: string | null;
  /** Whether index is outside page bounds */
  outsideBounds: boolean;
}

// ============================================================================
// PAGE GRAPHEME BOUNDARIES
// ============================================================================

/**
 * Extracts grapheme index boundaries from page lines.
 * 
 * @param lines - Array of lines with grapheme indices
 * @returns Page grapheme boundaries
 * 
 * @example
 * const lines = snapshot.snapshot?.lines || [];
 * const bounds = getPageGraphemeBounds(lines);
 * console.log(`Page spans graphemes ${bounds.pageStartGrapheme} to ${bounds.pageEndGrapheme}`);
 */
export function getPageGraphemeBounds<T extends GraphemeIndexedLine>(
  lines: T[]
): PageGraphemeBounds {
  if (!lines || lines.length === 0) {
    return {
      pageStartGrapheme: 0,
      pageEndGrapheme: 0,
      lineCount: 0,
      lastLineIsEmpty: false,
      lastLine: null
    };
  }

  const firstLine = lines[0];
  const lastLine = lines[lines.length - 1];
  
  const pageStartGrapheme = firstLine?.start_grapheme_index ?? 0;
  const pageEndGrapheme = lastLine?.end_grapheme_index ?? 0;
  const lastLineIsEmpty = lastLine 
    ? lastLine.start_grapheme_index === lastLine.end_grapheme_index 
    : false;

  return {
    pageStartGrapheme,
    pageEndGrapheme,
    lineCount: lines.length,
    lastLineIsEmpty,
    lastLine: lastLine || null
  };
}

// ============================================================================
// CURSOR-ON-PAGE DETECTION
// ============================================================================

/**
 * Checks if a cursor position is on a given page.
 * 
 * Handles the edge case where the cursor is at the start of an empty line
 * at the end of the page. For empty lines where start === end, the cursor
 * AT that position should be considered "on page".
 * 
 * @param cursorPosition - Global cursor position (grapheme index)
 * @param lines - Array of lines with grapheme indices
 * @returns Result object with cursor-on-page status and diagnostic info
 * 
 * @example
 * const result = isCursorOnPage(cursorPosition, snapshot.snapshot?.lines || []);
 * if (result.cursorOnPage) {
 *   updateCaretPosition(cursorPosition);
 *   caretRef.current?.show();
 * } else {
 *   caretRef.current?.hide();
 * }
 */
export function isCursorOnPage<T extends GraphemeIndexedLine>(
  cursorPosition: number,
  lines: T[]
): CursorOnPageResult {
  const bounds = getPageGraphemeBounds(lines);
  
  if (bounds.lineCount === 0) {
    return {
      cursorOnPage: false,
      bounds,
      cursorAtEmptyLineStart: false
    };
  }

  // Standard range check
  const inRange = cursorPosition >= bounds.pageStartGrapheme 
    && cursorPosition <= bounds.pageEndGrapheme;

  // Edge case: Cursor at start of empty last line
  // For empty lines where start === end, the cursor AT that position 
  // should be considered "on page" because the range is [start, end) exclusive.
  const cursorAtEmptyLineStart = bounds.lastLineIsEmpty 
    && bounds.lastLine !== null
    && cursorPosition === bounds.lastLine.start_grapheme_index;

  const cursorOnPage = inRange || cursorAtEmptyLineStart;

  return {
    cursorOnPage,
    bounds,
    cursorAtEmptyLineStart
  };
}

/**
 * Simple cursor-on-page check without empty line edge case handling.
 * Use this for cases where the edge case doesn't apply.
 * 
 * @param cursorPosition - Global cursor position (grapheme index)
 * @param lines - Array of lines with grapheme indices
 * @returns true if cursor is within page bounds
 */
export function isCursorOnPageSimple<T extends GraphemeIndexedLine>(
  cursorPosition: number,
  lines: T[]
): boolean {
  const bounds = getPageGraphemeBounds(lines);
  
  if (bounds.lineCount === 0) {
    return false;
  }

  return cursorPosition >= bounds.pageStartGrapheme 
    && cursorPosition <= bounds.pageEndGrapheme;
}

// ============================================================================
// CLICK GRAPHEME INDEX VALIDATION
// ============================================================================

/**
 * Validates and adjusts a grapheme index from a click event.
 * 
 * For multi-page documents, getGraphemeIndexFromClick may return a page-local index.
 * This function checks if the index is plausible for the page and adds the page
 * offset if necessary.
 * 
 * @param graphemeIndex - Raw grapheme index from click handler
 * @param lines - Array of lines with grapheme indices
 * @param pageIndex - Current page index (used for logging and adjustment logic)
 * @returns Validated grapheme index with diagnostic info
 * 
 * @example
 * let graphemeIndex = getGraphemeIndexFromClick(...);
 * const validated = validateClickGraphemeIndex(graphemeIndex, snapshotLines, pageIndex);
 * if (validated.warning) {
 *   console.warn(validated.warning);
 * }
 * graphemeIndex = validated.graphemeIndex;
 */
export function validateClickGraphemeIndex<T extends GraphemeIndexedLine>(
  graphemeIndex: number,
  lines: T[],
  pageIndex: number
): ValidatedGraphemeIndex {
  if (!lines || lines.length === 0) {
    return {
      graphemeIndex,
      wasAdjusted: false,
      warning: null,
      outsideBounds: false
    };
  }

  const bounds = getPageGraphemeBounds(lines);
  let adjustedIndex = graphemeIndex;
  let wasAdjusted = false;
  let warning: string | null = null;

  // If grapheme index is below page start, it's a local index - add page offset
  if (graphemeIndex < bounds.pageStartGrapheme && pageIndex > 0) {
    warning = `⚠️ [GraphemeIndexMapper] Page ${pageIndex}: Click returned local index ${graphemeIndex}, adding page offset ${bounds.pageStartGrapheme}`;
    adjustedIndex = graphemeIndex + bounds.pageStartGrapheme;
    wasAdjusted = true;
  }

  // Check if adjusted index is outside page bounds
  const outsideBounds = adjustedIndex < bounds.pageStartGrapheme 
    || adjustedIndex > bounds.pageEndGrapheme;

  if (outsideBounds && !warning) {
    warning = `⚠️ [GraphemeIndexMapper] Page ${pageIndex}: Grapheme ${adjustedIndex} outside page bounds [${bounds.pageStartGrapheme}, ${bounds.pageEndGrapheme}]`;
  }

  return {
    graphemeIndex: adjustedIndex,
    wasAdjusted,
    warning,
    outsideBounds
  };
}

// ============================================================================
// DIAGNOSTIC HELPERS
// ============================================================================

/**
 * Generates a diagnostic summary of line grapheme ranges.
 * Useful for debugging caret/cursor issues.
 * 
 * @param lines - Array of lines with grapheme indices
 * @returns Formatted string of line ranges
 * 
 * @example
 * console.log(formatLineRanges(lines));
 * // Output: "L0:[0-5), L1:[5-12), L2:[12-12)"
 */
export function formatLineRanges<T extends GraphemeIndexedLine>(
  lines: T[]
): string {
  if (!lines || lines.length === 0) {
    return 'none';
  }

  return lines
    .map((line, index) => `L${index}:[${line.start_grapheme_index}-${line.end_grapheme_index})`)
    .join(', ');
}

/**
 * Generates a diagnostic object for cursor resolution debugging.
 * 
 * @param cursorPosition - Current cursor position
 * @param lines - Array of lines with grapheme indices
 * @param pageIndex - Current page index
 * @returns Diagnostic object for logging
 */
export function getCursorResolutionDiagnostics<T extends GraphemeIndexedLine>(
  cursorPosition: number,
  lines: T[],
  pageIndex: number
): Record<string, unknown> {
  const result = isCursorOnPage(cursorPosition, lines);
  const { bounds } = result;

  return {
    cursorPosition,
    pageIndex,
    pageStartGrapheme: bounds.pageStartGrapheme,
    pageEndGrapheme: bounds.pageEndGrapheme,
    linesCount: bounds.lineCount,
    lastLineRange: bounds.lastLine 
      ? `[${bounds.lastLine.start_grapheme_index}, ${bounds.lastLine.end_grapheme_index})`
      : 'none',
    lastLineIsEmpty: bounds.lastLineIsEmpty,
    cursorAtEmptyLineStart: result.cursorAtEmptyLineStart,
    cursorOnThisPage: result.cursorOnPage,
    allLineRanges: formatLineRanges(lines)
  };
}
